import {
  classifyChatIntentHeuristics,
  classifyChatIntentAndContractWithModelFallback
} from '../aiIntentHelpers';
import { createStageResult } from './contracts';

// Classifies intent for the current turn and returns normalized intent stage payload.
export const executeIntentStage = async ({
  userText = '',
  mode = 'Explore',
  intentModel = null,
  routingMode = 'hybrid',
  signal,
  onIntentModelAttempt,
  classifyIntent = classifyChatIntentAndContractWithModelFallback,
  intentContext = null,
  alwaysOnIntentContract = false,
  forceOnLongPrompts = false,
  longPromptMinSearchableKeywords = 4,
  maxPrimaryKeywords = 8,
  maxSecondaryKeywords = 12,
  maxDocHints = 8
} = {}) => {
  const heuristic = classifyChatIntentHeuristics({ userText, mode });
  const useModelFallback = String(routingMode || 'hybrid').trim().toLowerCase() === 'hybrid';

  const intent = await classifyIntent({
    userText,
    mode,
    heuristicIntent: heuristic,
    enabled: useModelFallback,
    alwaysOn: Boolean(alwaysOnIntentContract),
    forceOnLongPrompts: Boolean(forceOnLongPrompts),
    longPromptMinSearchableKeywords: Number(longPromptMinSearchableKeywords || 4),
    model: intentModel || undefined,
    onModelAttempt: onIntentModelAttempt,
    contextBundle: intentContext,
    maxPrimaryKeywords,
    maxSecondaryKeywords,
    maxDocHints,
    signal
  });

  return createStageResult({
    stage: 'intent',
    status: 'ok',
    decisionCode: intent?.source === 'model-fallback' ? 'intent_model_fallback' : 'intent_heuristic',
    payload: {
      intentKey: intent?.key || 'ask',
      intentConfidence: Number(intent?.confidence || 0),
      intentSource: intent?.source || 'heuristic',
      intentModel: intentModel || null,
      contract: intent?.contract && typeof intent.contract === 'object' ? intent.contract : null,
      contractSource: String(intent?.contract?.source || '').trim() || 'heuristic',
      contractDecisionCode: String(intent?.contractMeta?.decisionCode || '').trim() || 'intent_contract_heuristic_default',
      contractModelAttempted: Boolean(intent?.contractMeta?.modelAttempted),
      usedModelFallback: Boolean(useModelFallback),
      primaryKeywordCount: Array.isArray(intent?.contract?.primaryKeywords) ? intent.contract.primaryKeywords.length : 0,
      secondaryKeywordCount: Array.isArray(intent?.contract?.secondaryKeywords) ? intent.contract.secondaryKeywords.length : 0,
      queryRewriteCount: Array.isArray(intent?.contract?.queryRewrites) ? intent.contract.queryRewrites.length : 0,
      docHintCount: Array.isArray(intent?.contract?.docHints) ? intent.contract.docHints.length : 0,
      budgetHintTier: String(intent?.contract?.budgetHint?.tier || '').trim() || null
    }
  });
};

// Runs intent stage orchestration and emits goal_update + intent stage payloads.
export const executeIntentAndGoalUpdateFlow = async ({
  chatTurn,
  userText = '',
  signal,
  canPersistGoalState = false,
  emitStage,
  onIntentModelAttempt,
  executeIntentStageFn = executeIntentStage,
  classifyIntent = classifyChatIntentAndContractWithModelFallback,
  mergeGoalState,
  summarizeGoalState,
  intentContext = null,
  intentContractConfig = null
} = {}) => {
  const canUseIntentFallback = Boolean(chatTurn?.runtime?.enabled)
    && chatTurn?.runtime?.routingMode === 'hybrid';

  const intentStage = await executeIntentStageFn({
    userText,
    mode: chatTurn?.mode,
    intentModel: chatTurn?.intentModel || chatTurn?.model || null,
    routingMode: chatTurn?.runtime?.routingMode || 'hybrid',
    signal,
    onIntentModelAttempt,
    classifyIntent,
    intentContext,
    alwaysOnIntentContract: Boolean(intentContractConfig?.alwaysOn),
    forceOnLongPrompts: intentContractConfig?.forceOnLongPrompts !== false,
    longPromptMinSearchableKeywords: Number(intentContractConfig?.longPromptMinSearchableKeywords || 4),
    maxPrimaryKeywords: Number(intentContractConfig?.maxPrimaryKeywords || 8),
    maxSecondaryKeywords: Number(intentContractConfig?.maxSecondaryKeywords || 12),
    maxDocHints: Number(intentContractConfig?.maxDocHints || 8),
  });

  const nextIntent = {
    key: intentStage?.payload?.intentKey || 'ask',
    confidence: Number(intentStage?.payload?.intentConfidence || 0),
    source: intentStage?.payload?.intentSource || 'heuristic',
    contract: intentStage?.payload?.contract && typeof intentStage.payload.contract === 'object'
      ? intentStage.payload.contract
      : (intentStage?.contract && typeof intentStage.contract === 'object' ? intentStage.contract : null)
  };

  const nextGoalState = Boolean(chatTurn?.runtime?.enabled) && typeof mergeGoalState === 'function'
    ? mergeGoalState({
      previousGoalState: chatTurn?.goalState,
      userText,
      intent: nextIntent,
      mode: chatTurn?.mode,
      contextPlan: chatTurn?.contextPlan
    })
    : chatTurn?.goalState;

  if (typeof emitStage === 'function') {
    emitStage('intent', {
      intentKey: intentStage?.payload?.intentKey || nextIntent.key || 'ask',
      intentConfidence: Number(intentStage?.payload?.intentConfidence || nextIntent.confidence || 0),
      intentSource: intentStage?.payload?.intentSource || nextIntent.source || 'heuristic',
      intentModel: intentStage?.payload?.intentModel || chatTurn?.intentModel || chatTurn?.model || null,
      intentContract: nextIntent.contract && typeof nextIntent.contract === 'object' ? nextIntent.contract : null,
      intentContractSource: intentStage?.payload?.contractSource || String(nextIntent?.contract?.source || '').trim() || 'heuristic',
      intentContractDecisionCode: intentStage?.payload?.contractDecisionCode || 'intent_contract_heuristic_default',
      intentContractModelAttempted: Boolean(intentStage?.payload?.contractModelAttempted),
      intentContractPrimaryKeywordCount: Array.isArray(nextIntent?.contract?.primaryKeywords) ? nextIntent.contract.primaryKeywords.length : 0,
      intentContractSecondaryKeywordCount: Array.isArray(nextIntent?.contract?.secondaryKeywords) ? nextIntent.contract.secondaryKeywords.length : 0,
      intentContractQueryRewriteCount: Array.isArray(nextIntent?.contract?.queryRewrites) ? nextIntent.contract.queryRewrites.length : 0,
      intentContractDocHintCount: Array.isArray(nextIntent?.contract?.docHints) ? nextIntent.contract.docHints.length : 0,
      usedModelFallback: intentStage?.payload?.usedModelFallback === undefined
        ? Boolean(canUseIntentFallback)
        : Boolean(intentStage.payload.usedModelFallback)
    });

    emitStage('goal_update', {
      goalStateEnabled: Boolean(canPersistGoalState),
      goalStateHydrated: Boolean(chatTurn?.goalStateHydrated),
      goalState: typeof summarizeGoalState === 'function' ? summarizeGoalState(nextGoalState) : null
    });
  }

  return {
    intentStage,
    intent: nextIntent,
    goalState: nextGoalState
  };
};
